import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAppContext } from "../../contexts";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useExchangeFetch, useAPI, useHardCode } from "../../hooks";
import {
  removeInsignificantZero,
  capitalizeFirstLetter,
} from "../../Utilities";
import {
  FullPageLoader,
  ContactInfoModal,
  ReviewExchangeSummary,
  ContactInfoCard,
  ItemCard,
  Toast,
} from "..";
import {
  updatePaymentSettings,
  updateOtherSettings,
  updateRefundSettingsDetails,
  updateExchangeSettingsDetails,
} from "../../redux/slices";
import {
  paymentSettingTypes,
  otherSettingsActionTypes,
  exchangeSettingsActionTypes,
  refundSettingsActionTypes,
} from "../../redux/utils";
import "./exchange_payment.css";
import { CURRENCY_SYMBOLS } from "../../Utilities/currency";

export const ExchangePayment = () => {
  const { itemToBeReturnedTags } = useExchangeFetch();
  const [priceDifference, setPriceDifference] = useState(false);
  const [availableCredit, setAvailableCredit] = useState(0);
  const [reverseShipmentCharges, setReverseShipmentCharges] = useState(0);
  const [refundAmount, setRefundAmount] = useState(0);
  const [dueAmount, setDueAmount] = useState(0);
  const [isLoader, setIsLoader] = useState(false);
  const [isFullScreenLoader, setIsFullScreenLoader] = useState(true);
  const {
    setIsBtnDisabled,
    isBtnDisabled,
    isInternalServerError,
    setIsInternalServerError,
  } = useAppContext();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const storeConfigState = useSelector((state) => state.storeSettings);
  const productDetailsState = useSelector((state) => state.productDetails);
  const selectedProductDetailsState = useSelector(
    (state) => state.selectedProductDetails
  );
  const { SHOPIFY_SHOP_OBJECT, CURRENT_STORE_NAME, PURE_FEMALE_TRANSLATION } =
    useHardCode();
  const otherSettingsState = useSelector((state) => state.otherSettings);
  const exchangeSettingsState = useSelector((state) => state.exchangeSettings);
  const {
    EXCHANGE_ITEM_PAYMENT_INFO_API,
    EXCHANGE_ITEM_VARIANT_ID_API,
    EXCHANGE_SUBMIT_API,
  } = useAPI();
  const FINAL_API_CALL =
    storeConfigState.platform === "shopify"
      ? `${EXCHANGE_ITEM_VARIANT_ID_API}${selectedProductDetailsState.variantID}/`
      : EXCHANGE_SUBMIT_API;
  const old_item_name = exchangeSettingsState.selectedProductName;
  const old_item_value =
    CURRENCY_SYMBOLS[exchangeSettingsState?.availableCreditCurrency] +
    removeInsignificantZero(parseFloat(exchangeSettingsState.availableCredit));

  const old_item_variants = itemToBeReturnedTags
    ?.substring(0, itemToBeReturnedTags?.length - 1)
    .replace(/,/g, "/");
  const new_item_name = exchangeSettingsState.newItemName;
  const new_item_value =
    CURRENCY_SYMBOLS[exchangeSettingsState.newItemPriceCurrency] +
    removeInsignificantZero(
      parseFloat(exchangeSettingsState.newItemPrice) *
        parseFloat(selectedProductDetailsState.selectedQuantity)
    );

  const colonSpace = " " + ":" + " ";
  const new_item_color =
    exchangeSettingsState?.variantLabels?.[0] +
    (exchangeSettingsState?.newItemVariations?.tags.length > 0
      ? exchangeSettingsState?.newItemVariations?.tags?.[0]
      : "");
  const new_item_size =
    exchangeSettingsState?.variantLabels?.[1] +
    exchangeSettingsState?.newItemVariations?.tags?.[1];
  const new_item_other =
    exchangeSettingsState?.variantLabels?.[2] +
    exchangeSettingsState?.newItemVariations?.tags?.[2];
  let new_item_variants =
    (new_item_color ? new_item_color + "/ " : "") +
    (new_item_size ? new_item_size + "/ " : "") +
    (new_item_other ? new_item_other : "");
  new_item_variants = new_item_variants
    ?.replace(/\s+$/, "")
    ?.replace(/\/$/, "");

  const itemValueLabel =
    storeConfigState?.themeConfig?.exchange_review_item_value_label +
    colonSpace +
    old_item_value;
  const priceDifferenceLabel =
    storeConfigState?.themeConfig?.exchange_review_price_difference_label +
    colonSpace +
    CURRENCY_SYMBOLS[productDetailsState.sessionCurrency] +
    removeInsignificantZero(parseFloat(priceDifference));
  const refundDueLabel =
    (refundAmount > 0
      ? storeConfigState?.themeConfig?.exchange_review_total_refund_label
      : storeConfigState?.themeConfig?.exchange_review_total_due_label) +
    colonSpace +
    CURRENCY_SYMBOLS[productDetailsState.sessionCurrency] +
    removeInsignificantZero(
      parseFloat(refundAmount > 0 ? refundAmount : dueAmount)
    );
  const [isModalOpen, setModalIsOpen] = useState(false);
  const toggleModal = () => {
    setModalIsOpen(!isModalOpen);
  };
  const refundSettingsState = useSelector((state) => state.refundSettings);
  useEffect(() => {
    (async () => {
      try {
        setIsBtnDisabled(true);

        let discounted_exchange_price = 0;
        let discounted_exchange_currency = false;
        if (storeConfigState.platform === "shopify") {
          try {
            const cart_response = await axios.post("/cart/add.js", {
              id: exchangeSettingsState.exchangeVariantID,
              quantity: parseInt(selectedProductDetailsState.selectedQuantity),
            });
            if (cart_response?.status === 200) {
              discounted_exchange_price =
                cart_response.data.discounted_price / 100;
              dispatch(
                updateExchangeSettingsDetails({
                  key: exchangeSettingsActionTypes.UPDATE_NEW_ITEM_PRICE,
                  value: removeInsignificantZero(
                    parseFloat(discounted_exchange_price)
                  ),
                })
              );
              const cart_update_response = await axios.post("/cart/update.js", {
                updates: {
                  [exchangeSettingsState.exchangeVariantID]: 0,
                },
              });
              if (cart_update_response?.status === 200) {
                discounted_exchange_currency =
                  cart_update_response.data.currency;
                dispatch(
                  updateExchangeSettingsDetails({
                    key: exchangeSettingsActionTypes.UPDATE_NEW_ITEM_PRICE_CURRENCY,
                    value: discounted_exchange_currency,
                  })
                );
              }
            }
          } catch (error) {
            console.log(error);
          }
        }

        const response = await axios.post(EXCHANGE_ITEM_PAYMENT_INFO_API, {
          store: storeConfigState.storeUniqueID,
          old_variant_id: selectedProductDetailsState.variantID,
          old_product_id: selectedProductDetailsState.productID,
          new_variant_id: exchangeSettingsState.exchangeVariantID,
          new_product_id: exchangeSettingsState.exchangeProductID,
          order_id: productDetailsState.recentOrderID,
          order_number: productDetailsState.orderNumber,
          email: productDetailsState.customerEmail,
          test_mode: productDetailsState.testMode,
          line_item_id: selectedProductDetailsState.lineItemID,
          // new_item_quantity: parseInt(exchangeSettingsState.newItemQty),
          quantity: parseInt(selectedProductDetailsState.selectedQuantity),
          currency: productDetailsState.sessionCurrency,
          discounted_exchange_price: discounted_exchange_price,
          discounted_exchange_currency: discounted_exchange_currency,
        });

        if (response?.status === 200) {
          setIsFullScreenLoader(false);
          setAvailableCredit(response?.data?.available_credit);
          setReverseShipmentCharges(response?.data?.reverse_shipment_charges);
          setRefundAmount(response?.data?.refund_amount);
          setDueAmount(response?.data?.due_amount);
          setPriceDifference(response?.data?.price_difference);
        }
        setIsBtnDisabled(false);
      } catch (error) {
        if (error?.response?.status === 500) {
          setIsFullScreenLoader(false);
          setIsLoader(false);
          setIsInternalServerError(true);
        }
        setIsBtnDisabled(false);
      }
    })();
  }, [navigate]);

  const submitExchangeHandler = (e) => {
    (async () => {
      try {
        setIsLoader(true);
        setIsBtnDisabled(true);
        const response = await axios.post(FINAL_API_CALL, {
          customer_id: productDetailsState.customerID,
          shop: storeConfigState.storeUniqueID,
          incentive: refundSettingsState.incentive,
          variant_id: selectedProductDetailsState.variantID,
          product_id: selectedProductDetailsState.productID,
          selected_variant_for_exchange:
            exchangeSettingsState.exchangeVariantID,
          selected_product_for_exchange:
            exchangeSettingsState.exchangeProductID,
          image_list: exchangeSettingsState.imageList,
          primary_reason: productDetailsState.returnReasonID,
          customer_email: productDetailsState.customerEmail,
          test_mode: productDetailsState.testMode,
          order_id: productDetailsState.recentOrderID,
          order_number: productDetailsState.orderNumber,
          line_item_id: selectedProductDetailsState.lineItemID,
          other_reason_desc: productDetailsState.reasonDetails,
          sub_reason_detail: productDetailsState.subReasonDetails,
          // new_item_quantity: parseInt(exchangeSettingsState.newItemQty),
          quantity: parseInt(selectedProductDetailsState.selectedQuantity),
          access_code: otherSettingsState.accessCode,
          customer_data: productDetailsState.customerInfo,
          currency: productDetailsState.sessionCurrency,
          discounted_exchange_price: exchangeSettingsState.newItemPrice,
          discounted_exchange_currency:
            exchangeSettingsState.newItemPriceCurrency,
        });

        if (response?.status === 200) {
          setIsLoader(false);
          dispatch(
            updatePaymentSettings({
              key: paymentSettingTypes.UPDATE_PAYMENT_REQUIRED,
              value: response.data.payment_required,
            })
          );

          dispatch(
            updatePaymentSettings({
              key: paymentSettingTypes.UPDATE_PAYMENT_LINK,
              value: response.data.payment_link,
            })
          );

          dispatch(
            updateRefundSettingsDetails({
              key: refundSettingsActionTypes.UPDATE_COLLECT_FROM_RAZORPAY,
              value: response.data.collect_through_razorpay,
            })
          );

          dispatch(
            updateRefundSettingsDetails({
              key: refundSettingsActionTypes.UPDATE_COLLECT_PAYMENT_THROUGH,
              value: response.data?.collect_payment_through,
            })
          );

          dispatch(
            updateRefundSettingsDetails({
              key: refundSettingsActionTypes.UPDATE_RAZORPAY_DETAILS,
              value: response.data.razorpay_object,
            })
          );

          dispatch(
            updatePaymentSettings({
              key: paymentSettingTypes.UPDATE_PAYMENT_ID,
              value: response.data.id,
            })
          );

          dispatch(
            updatePaymentSettings({
              key: paymentSettingTypes.UPDATE_COUPON_CODE,
              value: response.data.coupon_code,
            })
          );

          dispatch(
            updateOtherSettings({
              key: otherSettingsActionTypes.UPDATE_RAN,
              value: response.data.ran,
            })
          );
          setIsBtnDisabled(false);
          navigate("/ExchangeSubmitted");
        }
      } catch (error) {
        if (error?.response?.status === 500) {
          setIsLoader(false);
          setIsInternalServerError(true);
        }
        setIsBtnDisabled(false);
      }
    })();
  };

  /* navigate to previous page */
  const backHandler = () => {
    window.history.back();
  };

  return (
    <>
      {isFullScreenLoader ? (
        <FullPageLoader />
      ) : (
        <main className="p21 choose">
          {isInternalServerError && <Toast type="error" />}
          <div className="saara__container">
            <div className="exchange_payment_row_title_main">
              <Link
                to=""
                onClick={backHandler}
                className="back_btn_small_screen"
              >
                <img src="assets/svg/chevron_left.svg" alt="back" />
              </Link>
              <h1 className="p21__title saara-title exchange_payment_title">
                {storeConfigState?.themeConfig?.exchange_review_page_left_title}
              </h1>
            </div>
            <div className="exchange_review_main">
              <div className="exchange_review_main_content">
                <div className="exchange_review_left">
                  <div className="exchange_review_summary">
                    <ReviewExchangeSummary
                      className="exchange-summary-card-content"
                      itemValue={itemValueLabel}
                      priceDifference={priceDifferenceLabel}
                      reverseShipment={reverseShipmentCharges}
                      dueOrRefund={refundDueLabel}
                    />
                  </div>
                  <div className="exchange_review_contact_info">
                    <ContactInfoCard
                      className="exchange-contact-card-content"
                      toggleModal={toggleModal}
                    />
                  </div>
                </div>
                <div className="item_card_main">
                  <ItemCard
                    label={
                      SHOPIFY_SHOP_OBJECT === CURRENT_STORE_NAME.pure_female
                        ? PURE_FEMALE_TRANSLATION.exchange_text.old
                        : storeConfigState?.themeConfig
                            ?.exchange_review_old_product_tag_label
                    }
                    imageSrc={
                      selectedProductDetailsState.selectedProductImage ||
                      otherSettingsState.defaultImage
                    }
                    value={
                      selectedProductDetailsState.shopifyRedirection
                        ? `Price:${old_item_value}`
                        : `${
                            SHOPIFY_SHOP_OBJECT ===
                            CURRENT_STORE_NAME.pure_female
                              ? PURE_FEMALE_TRANSLATION.exchange_text
                                  .exchange_value
                              : storeConfigState?.themeConfig
                                  ?.exchange_product_detail_right_card_title
                          }: ${old_item_value} `
                    }
                    name={old_item_name}
                    variants={old_item_variants}
                    labelClass="old_item_label"
                    itemQty={selectedProductDetailsState.selectedQuantity}
                    redirectedFromShopify={
                      selectedProductDetailsState.shopifyRedirection
                    }
                  />
                  <ItemCard
                    label={
                      SHOPIFY_SHOP_OBJECT === CURRENT_STORE_NAME.pure_female
                        ? PURE_FEMALE_TRANSLATION.exchange_text.new
                        : storeConfigState?.themeConfig
                            ?.exchange_review_new_product_tag_label
                    }
                    imageSrc={
                      selectedProductDetailsState.shopifyRedirection
                        ? selectedProductDetailsState.shopifySelectedProductImage
                        : exchangeSettingsState?.newItemVariations?.img_url ||
                          otherSettingsState.defaultImage
                    }
                    value={
                      selectedProductDetailsState.shopifyRedirection
                        ? `Price:${new_item_value}`
                        : `${
                            SHOPIFY_SHOP_OBJECT ===
                            CURRENT_STORE_NAME.pure_female
                              ? PURE_FEMALE_TRANSLATION.exchange_text
                                  .exchange_value
                              : storeConfigState?.themeConfig
                                  ?.exchange_review_item_value_label
                          }: ${new_item_value} `
                    }
                    name={new_item_name}
                    itemQty={exchangeSettingsState.newItemQty}
                    variants={
                      selectedProductDetailsState.shopifyRedirection
                        ? exchangeSettingsState.newItemVariantsShopify
                        : new_item_variants
                    }
                    labelClass="new_item_label"
                    redirectedFromShopify={
                      selectedProductDetailsState.shopifyRedirection
                    }
                  />
                </div>
              </div>
              <div className=" saara-justify-content-center">
                <div className="btn__big_screen_ep exchange_payment_button">
                  <button
                    className="hide-mobile saara_custom_btn saara__btn btn__back_ep back_page exchange-payment-back"
                    onClick={backHandler}
                    disabled={isBtnDisabled}
                  >
                    {storeConfigState?.themeConfig?.back_button_label}
                  </button>

                  <button
                    type="button"
                    className="saara_custom_btn form__btn continue-payment"
                    onClick={(e) => {
                      submitExchangeHandler(e);
                    }}
                    disabled={isBtnDisabled}
                  >
                    {isLoader ? (
                      <div
                        className="saara-spinner-border saara-spinner-border-sm"
                        role="status"
                      >
                        <span className="saara-visually-hidden"></span>
                      </div>
                    ) : refundAmount > 0 || dueAmount === 0 ? (
                      storeConfigState?.themeConfig
                        ?.exchange_review_page_submit_button_label
                    ) : (
                      storeConfigState?.themeConfig
                        ?.exchange_review_page_pay_button_label
                    )}
                  </button>

                  <br />
                </div>
              </div>
            </div>
          </div>
          {/*Modal */}
          {isModalOpen && (
            <div className="saara-modal-backdrop">
              <ContactInfoModal onRequestClose={toggleModal} />
            </div>
          )}
        </main>
      )}
    </>
  );
};
